<template>
  <ODialog
    ref="dialog"
    :title="$t('components.service.actions.removeForm.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.confirm'), type: 'confirm', variant: 'danger', callback: confirm },
    ]"
    :portal="portal"
  />
</template>

<script setup lang="ts">
import type { Service } from '~/_api/models/service'

interface ServiceForm {
  form?: {
    title: string
    idForm: number
  }
}

interface Props {
  service: Service
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
  (e: 'update:service', service: Service): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false
})

const emit = defineEmits<Emits>()
const { $i18n, $notification, $api } = useNuxtApp()

const dialog = ref()
const serviceForm = ref<ServiceForm | null>(null)

const info = computed(() => {
  return $i18n.t('components.service.actions.removeForm.info', { 
    title: serviceForm.value?.form?.title, 
    id: serviceForm.value?.form?.idForm 
  })
})

const open = (form: ServiceForm) => {
  serviceForm.value = form
  dialog.value.open()
}

const confirm = async () => {
  emit('update:loading', true)

  try {
    const response = await $api.service.removeForm(props.service, serviceForm.value)
    const service = response.data
    emit('update:loading', false)

    if (service) {
      $notification.toast({
        title: $i18n.t('components.service.actions.removeForm.success'),
        text: $i18n.t('components.service.actions.removeForm.successInfo', { title: serviceForm.value?.form?.title }),
        variant: 'success',
      })

      emit('success')
      emit('update:service', service)

      return true
    }
  } catch (e) {
    console.log(e)
    $notification.toast({
      title: $i18n.t('components.service.actions.removeForm.error'),
      text: $i18n.t('components.service.actions.removeForm.errorInfo', { title: serviceForm.value?.form?.title }),
      variant: 'danger',
    })

    emit('error')
  }
}

defineExpose({
  open
})
</script>
